/**
 * B-new-1008 — single locale-resolution helper for the bare-root visit.
 *
 * Order (per the task spec): the user's saved locale (i18next's own
 * localStorage cache, key `i18nextLng` — see `src/lib/i18n.ts`'s
 * `detection.caches: ['localStorage']`, and the package default
 * `lookupLocalStorage: 'i18nextLng'` in i18next-browser-languagedetector)
 * → the browser's language, if it's one of the 8 supported locales →
 * `en` as the single default.
 *
 * This does NOT replace i18next's own LanguageDetector (which still drives
 * `i18n.language` for every other route) — it exists because the bare `/`
 * route needs a locale to build a redirect URL to `/$locale/...` BEFORE any
 * $locale-scoped route (and its i18n.changeLanguage side effect) has
 * mounted.
 */

// B-new-1289: SUPPORTED_LOCALES/SupportedLocale used to be redeclared here
// (identical 8-code array, same order) — now imported from the canonical
// source in @xeito/shared-utils instead of duplicated.
import { SUPPORTED_LOCALES, type SupportedLocale } from '@xeito/shared-utils'
export { SUPPORTED_LOCALES }
export type { SupportedLocale }

export const DEFAULT_LOCALE: SupportedLocale = 'en'

// Normalizes a BCP-47-ish tag ('es', 'es-ES', 'ES_es') down to a supported
// locale code, or undefined if it isn't one of the 8. Handles the saved
// value AND every browser-language entry through the same rule, so a
// region-tagged cached value (e.g. a stale 'es-ES' some older build wrote)
// matches exactly like a region-tagged browser language would.
function toSupportedLocale(tag: string | null | undefined): SupportedLocale | undefined {
  if (!tag) return undefined
  const short = tag.trim().slice(0, 2).toLowerCase()
  return (SUPPORTED_LOCALES as readonly string[]).includes(short) ? (short as SupportedLocale) : undefined
}

export function resolveLocale(opts: {
  saved?: string | null
  browserLanguages?: readonly string[] | null
}): SupportedLocale {
  const fromSaved = toSupportedLocale(opts.saved)
  if (fromSaved) return fromSaved

  for (const lang of opts.browserLanguages ?? []) {
    const fromBrowser = toSupportedLocale(lang)
    if (fromBrowser) return fromBrowser
  }

  return DEFAULT_LOCALE
}

// i18next-browser-languagedetector's default localStorage key
// (`lookupLocalStorage: 'i18nextLng'`), reused rather than re-invented.
const SAVED_LOCALE_STORAGE_KEY = 'i18nextLng'

export function readSavedLocale(): string | null {
  if (typeof window === 'undefined') return null
  try {
    return window.localStorage.getItem(SAVED_LOCALE_STORAGE_KEY)
  } catch {
    return null
  }
}

export function readBrowserLanguages(): readonly string[] {
  if (typeof navigator === 'undefined') return []
  if (navigator.languages && navigator.languages.length) return navigator.languages
  return navigator.language ? [navigator.language] : []
}

export function resolveRootLocale(): SupportedLocale {
  return resolveLocale({ saved: readSavedLocale(), browserLanguages: readBrowserLanguages() })
}
